import { BookOpen } from 'lucide-react'

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger, Badge } from '@fortem-monorepo/react-ui'

export const AccruedInterestGuide = () => (
  <Accordion type="single" collapsible className="mx-3 mt-2 shrink-0 rounded-lg border bg-muted/20 px-4">
    <AccordionItem value="ops-guide">
      <AccordionTrigger className="items-center py-3 hover:no-underline">
        <span className="flex items-center gap-2">
          <BookOpen aria-hidden="true" className="size-4 text-muted-foreground" />
          How to use this page
        </span>
      </AccordionTrigger>
      <AccordionContent className="pb-3">
        <div
          tabIndex={0}
          aria-label="Accrued interests operating guide"
          className="max-h-96 space-y-4 overflow-y-auto rounded-sm pr-2 text-sm leading-6 focus-visible:outline-2 focus-visible:outline-ring"
        >
          <p className="text-muted-foreground">
            Choose a fund above. For daily review and balance confirmation, also choose a value date. For the chart,
            choose an ISIN to explore its saved history.
          </p>
          <div className="rounded-md border-l-2 border-primary/40 bg-muted/50 px-3 py-2">
            <strong>When are changes saved?</strong> Daily corrections save immediately after confirmation. Balance
            edits stay pending until you choose <strong>Confirm displayed balances</strong>.
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <section aria-labelledby="accrual-guide-daily" className="rounded-lg border bg-background p-4">
              <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Step 1 · Daily accrued interests
              </p>
              <h4 id="accrual-guide-daily" className="mb-3 text-base font-semibold">
                Review daily accruals
              </h4>
              <ol className="list-decimal space-y-2 pl-5 marker:font-semibold">
                <li>
                  <strong>Compare amounts.</strong> Review Daily Accrual Local against 5-day Avg Local. The average
                  matches the accrual period, including three-day Mondays.
                </li>
                <li>
                  <strong>Investigate differences.</strong> Use Check and NEW tags to prioritise review. Expand an
                  instrument to inspect its trades.
                </li>
                <li>
                  <strong>Correct a trade.</strong> Right-click, choose Correct daily accrued interest and enter the
                  amount and a reason of 11–200 characters. Review and confirm to save.
                </li>
              </ol>
            </section>
            <section aria-labelledby="accrual-guide-balances" className="rounded-lg border bg-background p-4">
              <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Step 2 · Accrued interest balances
              </p>
              <h4 id="accrual-guide-balances" className="mb-3 text-base font-semibold">
                Confirm balances
              </h4>
              <ol className="list-decimal space-y-2 pl-5 marker:font-semibold">
                <li>
                  <strong>Review the roll-forward.</strong> Compare prior saved, opening, daily and closing amounts.
                  Check coupon tags; resets can explain differences. Balances can only be computed for today or earlier,
                  using the London date.
                </li>
                <li>
                  <strong>Keep corrections pending.</strong> Right-click a trade to edit opening and closing balances.
                  Add a reason of 11–200 characters, review and choose Keep pending correction.
                </li>
                <li>
                  <strong>Confirm the batch.</strong> Choose Confirm displayed balances. Review the fund, date and
                  amounts. This saves all trades remaining after grid filters, including collapsed groups.
                </li>
              </ol>
            </section>
          </div>

          <section aria-labelledby="accrual-guide-chart" className="rounded-lg border bg-background p-4">
            <h4 id="accrual-guide-chart" className="mb-2 font-semibold">
              Explore saved balance history
            </h4>
            <p>
              Open Balances As Chart and select an ISIN. Only instruments with saved balances for the selected fund are
              listed. The line sums saved closing balances by date across its trades; the Date filter does not apply.
            </p>
            <p className="mt-2 text-muted-foreground">
              Yellow lines mark scheduled coupon payments. Green triangles mark buys; red triangles mark sells on their
              UTC execution date. Their height indicates the side, not a balance amount. Hover for exact values and
              trade details. Missing amounts leave gaps. The chart is read only.
            </p>
            <p className="mt-2 text-muted-foreground">
              The table beneath the chart lists balance, coupon and trade datapoints in pages of 50 rows. Drag the
              divider to resize it. Choose Email all datapoints to me to receive a CSV at your account email address;
              this includes all chart data, regardless of table filters or the current page. If the saved history has
              changed, refresh the chart before sending.
            </p>
          </section>

          <section aria-labelledby="accrual-guide-tags" className="rounded-lg border bg-background p-4">
            <h4 id="accrual-guide-tags" className="mb-3 font-semibold">
              Read the tags
            </h4>
            <div className="grid gap-x-8 gap-y-2 md:grid-cols-2">
              <dl className="space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <dt>
                    <Badge theme="success" className="text-green-800">
                      In Line
                    </Badge>
                  </dt>
                  <dd>Difference below 1%</dd>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <dt>
                    <Badge theme="warning" className="text-amber-900">
                      Minor
                    </Badge>
                  </dt>
                  <dd>Difference from 1% to 5%</dd>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <dt>
                    <Badge theme="destructive" className="text-red-800">
                      Investigate
                    </Badge>
                  </dt>
                  <dd>Difference above 5%</dd>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <dt>
                    <Badge theme="success" className="text-green-800">
                      NEW
                    </Badge>
                  </dt>
                  <dd>Instrument traded in the past three days</dd>
                </div>
              </dl>
              <dl className="space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <dt>
                    <Badge theme="warning" className="text-amber-900">
                      Coupon Reset in 2 days
                    </Badge>
                  </dt>
                  <dd>Upcoming coupon</dd>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <dt>
                    <Badge theme="success" className="text-green-800">
                      Coupon Reset Today
                    </Badge>
                  </dt>
                  <dd>Coupon on the selected date</dd>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <dt>
                    <Badge variant="outline" className="border-purple-300 bg-purple-100 text-purple-800">
                      Coupon Reset 2 days ago
                    </Badge>
                  </dt>
                  <dd>Past coupon</dd>
                </div>
              </dl>
            </div>
            <p className="mt-3 border-t pt-2 text-muted-foreground">
              Check uses exact, unrounded amounts. Blank means no comparison is available. A nonzero accrual against a
              zero average is Investigate.
            </p>
          </section>

          <section aria-labelledby="accrual-guide-finish" className="space-y-2 px-1">
            <h4 id="accrual-guide-finish" className="font-semibold">
              Before you confirm
            </h4>
            <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
              <li>
                <strong className="text-foreground">Check the trade detail.</strong> Group rows show totals. Correction
                dialogs show full precision; a dash means unknown, not zero.
              </li>
              <li>
                <strong className="text-foreground">Resolve pending edits.</strong> Preview and Pending need
                confirmation. Saved manual corrections are preserved. Review required means recheck the correction or
                discard and refresh.
              </li>
              <li>
                <strong className="text-foreground">Keep your selection in mind.</strong> Tabs retain pending edits;
                filter changes and balance refreshes require discarding them. After a save error, refresh and check
                saved values before retrying.
              </li>
            </ul>
            <p className="text-xs text-muted-foreground">
              Corrections and confirmation require Operations write access. Balances show instruments with daily
              interest on the selected date or previous weekday. Prior saved dates may be older; no prior balance means
              a zero opening.
            </p>
          </section>
        </div>
      </AccordionContent>
    </AccordionItem>
  </Accordion>
)
